import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { 
    collection, 
    query, 
    orderBy, 
    limit, 
    onSnapshot 
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

const Shrine = ({ user, appConfig, onBack }) => {
    // モード: 'wish'(祈願) or 'exorcism'(厄除け)
    const [mode, setMode] = useState('wish');
    const [posts, setPosts] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // お賽銭額のState (初期値5)
    const [offeringAmount, setOfferingAmount] = useState(5);

    // 設定値の取得
    const shrineName = appConfig?.shrineName || '勝守神社';
    
    // 神社名がURLかどうか判定
    const isShrineLogo = shrineName.startsWith('http');

    const bannerUrl = appConfig?.shrineBannerUrl || 'https://images.unsplash.com/photo-1590416992921-2e67a5050c26?auto=format&fit=crop&q=80&w=1200';
    const bgUrl = appConfig?.shrineBgUrl || ''; 
    const pointUnit = appConfig?.pointUnitLabel || 'RB';

    // テーマ設定
    const theme = {
        wish: {
            primary: '#d32f2f', // 朱色
            accent: '#ffb300', // 金
            bg: 'rgba(255, 248, 225, 0.95)',
            btn: 'linear-gradient(45deg, #d32f2f, #ff5252)',
            placeholder: '試合の勝利、個人の目標、願い事を奉納...',
            icon: '🙏',
            label: '必勝祈願'
        },
        exorcism: {
            primary: '#4a148c', // 紫
            accent: '#e040fb', // ネオン紫
            bg: 'rgba(243, 229, 245, 0.95)',
            btn: 'linear-gradient(45deg, #4a148c, #7b1fa2)',
            placeholder: '日頃の鬱憤、ミスの懺悔、厄をここに捨てる...',
            icon: '⚡',
            label: '厄除け・愚痴'
        }
    };
    const currentTheme = theme[mode];

    // 投稿の監視 (最新5件のみ取得)
    useEffect(() => {
        const q = query(
            collection(db, 'jinja_posts'), 
            orderBy('createdAt', 'desc'), 
            limit(5)
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPosts(data);
        });

        return () => unsubscribe();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputText.trim()) return;
        if (!user) { alert('参拝にはログインが必要です'); return; }

        // 金額チェック
        const amount = Number(offeringAmount);
        if (amount < 5) {
            alert(`お賽銭は 5 ${pointUnit} 以上から受け付けております。`);
            return;
        }

        // 確認ダイアログ
        const confirmMsg = `【お賽銭の確認】\n\n${amount} ${pointUnit} を奉納して、\n絵馬を掛けますか？`;
        if (!window.confirm(confirmMsg)) return;

        setIsSubmitting(true);

        try {
            // ★ 奉納処理は Cloud Functions に任せる。
            //   以前はクライアントから直接 points を減らしていたため、
            //   金額を偷って会計だけ水増しさせることができた。
            const fns = getFunctions(getApp(), 'asia-northeast1');
            await httpsCallable(fns, 'offerToShrine')({
                amount: amount,
                text: inputText,
                mode: mode
            });

            setInputText('');
            alert(`奉納しました！\n(-${amount} ${pointUnit})`);

        } catch (error) {
            console.error("奉納エラー:", error?.code || '', error?.message || error);
            alert(error?.message || "奉納に失敗しました。もう一度お試しください。");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            backgroundColor: bgUrl ? '#333' : (mode === 'wish' ? '#fff8e1' : '#212121'),
            position: 'relative',
            paddingBottom: '100px', // 下部メニュー用に余白確保
            fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif'
        }}>
            {/* --- ヘッダーエリア --- */}
            <div style={{position: 'relative', height: '220px', width: '100%'}}>
                <img 
                    src={bannerUrl} 
                    alt="Shrine Banner" 
                    style={{width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.8)'}} 
                />
                
                {/* 戻るボタン (左上) */}
                <button onClick={onBack} style={{
                    position: 'absolute', top: '15px', left: '15px', 
                    background: 'rgba(255,255,255,0.9)', color: '#333', border: 'none', 
                    borderRadius: '30px', padding: '8px 16px', fontSize: '12px', fontWeight: 'bold',
                    cursor:'pointer', zIndex:10, display: 'flex', alignItems: 'center', gap: '5px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                }}>
                    <span>🏠</span> アプリに戻る
                </button>

                {/* 神社名プレート (画像 or テキスト) */}
                <div style={{
                    position: 'absolute', bottom: '-20px', left: '50%', transform: 'translateX(-50%)',
                    background: '#fff', padding: '10px 30px', borderRadius: '4px',
                    border: `4px solid ${currentTheme.primary}`,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    textAlign: 'center', minWidth: '200px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    {isShrineLogo ? (
                        <img 
                            src={shrineName} 
                            alt="Shrine Logo" 
                            style={{maxWidth: '180px', maxHeight: '50px', objectFit: 'contain'}} 
                        />
                    ) : (
                        <h1 style={{margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#333'}}>{shrineName}</h1>
                    )}
                </div>
            </div>

            {/* --- メインコンテンツ --- */}
            <div style={{maxWidth: '600px', margin: '40px auto 0', padding: '0 20px'}}>
                
                {/* モード切替タブ */}
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: '20px'}}>
                    <button 
                        onClick={() => setMode('wish')}
                        style={{
                            flex: 1, padding: '12px', border: 'none', cursor: 'pointer',
                            background: mode === 'wish' ? theme.wish.primary : '#eee',
                            color: mode === 'wish' ? '#fff' : '#999',
                            fontWeight: 'bold', borderRadius: '8px 0 0 8px',
                            transition: 'all 0.3s'
                        }}
                    >
                        🙏 祈願 (表)
                    </button>
                    <button 
                        onClick={() => setMode('exorcism')}
                        style={{
                            flex: 1, padding: '12px', border: 'none', cursor: 'pointer',
                            background: mode === 'exorcism' ? theme.exorcism.primary : '#eee',
                            color: mode === 'exorcism' ? '#fff' : '#999',
                            fontWeight: 'bold', borderRadius: '0 8px 8px 0',
                            transition: 'all 0.3s'
                        }}
                    >
                        ⚡ 厄除け (裏)
                    </button>
                </div>

                {/* 投稿フォーム */}
                <div style={{
                    background: currentTheme.bg, padding: '20px', borderRadius: '12px',
                    border: `2px solid ${currentTheme.primary}`,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    marginBottom: '30px'
                }}>
                    <div style={{textAlign: 'center', marginBottom: '15px', color: currentTheme.primary, fontWeight: 'bold'}}>
                        {mode === 'wish' ? '願いを絵馬に刻む' : '心の闇を吐き出す'}
                    </div>
                    <form onSubmit={handleSubmit}>
                        <textarea
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder={currentTheme.placeholder}
                            rows={3}
                            style={{
                                width: '100%', padding: '12px', borderRadius: '8px',
                                border: '1px solid #ccc', outline: 'none',
                                boxSizing: 'border-box', fontFamily: 'inherit',
                                fontSize: '16px', resize: 'none',
                                background: 'white'
                            }}
                        />
                        
                        {/* 金額入力エリア */}
                        <div style={{marginTop:'15px', background:'rgba(255,255,255,0.6)', padding:'10px', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'10px'}}>
                            <label style={{fontSize:'12px', fontWeight:'bold', color: currentTheme.primary}}>お賽銭額:</label>
                            <input 
                                type="number" 
                                min="5" 
                                value={offeringAmount} 
                                onChange={(e) => setOfferingAmount(e.target.value)}
                                style={{width:'60px', padding:'5px', textAlign:'right', borderRadius:'4px', border:'1px solid #ccc'}}
                            />
                            <span style={{fontSize:'12px', fontWeight:'bold'}}>{pointUnit}</span>
                        </div>

                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px'}}>
                            <div style={{fontSize: '11px', color: '#666'}}>
                                <div>{mode === 'wish' ? `📛 ${user?.displayName || 'ゲスト'}` : '🕵️ 完全匿名'}</div>
                            </div>
                            <button type="submit" disabled={isSubmitting} style={{
                                padding: '10px 25px', borderRadius: '30px', border: 'none',
                                background: currentTheme.btn, color: 'white', fontWeight: 'bold',
                                cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            }}>
                                {isSubmitting ? '処理中...' : `奉納する`}
                            </button>
                        </div>
                    </form>
                </div>

                {/* タイムライン (最新5件) */}
                <div style={{marginTop: '40px'}}>
                    <h3 style={{
                        textAlign: 'center', fontSize: '16px', color: mode === 'exorcism' && bgUrl ? '#fff' : '#444',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
                    }}>
                        <span>📜</span> みんなの{currentTheme.label} (最新5件)
                    </h3>
                    
                    <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                        {posts.map((post) => (
                            <div key={post.id} className="post-card" style={{
                                background: post.type === 'wish' 
                                    ? '#fff' 
                                    : 'linear-gradient(to right, #4a148c, #311b92)',
                                color: post.type === 'wish' ? '#333' : '#fff',
                                padding: '15px',
                                borderRadius: '8px',
                                borderLeft: `5px solid ${post.type === 'wish' ? '#d32f2f' : '#ea80fc'}`,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                animation: 'fadeIn 0.5s ease'
                            }}>
                                <div style={{fontSize: '15px', fontWeight: 'bold', marginBottom: '8px', whiteSpace: 'pre-wrap'}}>
                                    {post.text}
                                </div>
                                <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', fontSize: '11px', opacity: 0.8}}>
                                    {post.photoUrl && (
                                        <img src={post.photoUrl} alt="" style={{width: '18px', height: '18px', borderRadius: '50%', marginRight: '5px'}} />
                                    )}
                                    <span>{post.displayName}</span>
                                    <span style={{margin: '0 5px'}}>•</span>
                                    <span>{post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '今'}</span>
                                </div>
                            </div>
                        ))}
                        {posts.length === 0 && (
                            <div style={{textAlign: 'center', color: '#999', padding: '20px', background: 'rgba(255,255,255,0.5)', borderRadius: '8px'}}>
                                まだ投稿はありません
                            </div>
                        )}
                    </div>
                    {/* 古いログについての注釈 */}
                    <div style={{textAlign:'center', marginTop:'20px', fontSize:'10px', color: mode === 'exorcism' && bgUrl ? '#ccc' : '#999'}}>
                        ※古い奉納・厄除けは川へ流されます（自動的に非表示になります）
                    </div>
                </div>
            </div>

            {/* 下部メニュー (ホームに戻るボタン) */}
            <div style={{
                position: 'fixed', bottom: 0, left: 0, width: '100%', height: '70px',
                background: '#fff', borderTop: '1px solid #eee',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 999, boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
            }}>
                <button onClick={onBack} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#333',
                    padding: '0 20px'
                }}>
                    <span style={{fontSize: '24px'}}>🏠</span>
                    <span style={{fontSize: '11px', fontWeight: 'bold', marginTop: '4px'}}>ホームに戻る</span>
                </button>
            </div>
            
            {/* 簡単なCSSアニメーション定義 */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default Shrine;